import type { CamelCase } from "type-fest";

/**
 * Type representing a camelCase method name.
 * Used to enforce type safety when working with method names.
 */
export type ToMethodName<T extends string = string> = CamelCase<
  T,
  { preserveConsecutiveUppercase: false }
>;

const SEPARATOR_PATTERN = /[-\s_]+/;

/**
 * Converts a kebab-case, space-separated, or mixed-case string to camelCase.
 * Handles common separators: hyphens, spaces, and underscores.
 *
 * @param input - The string to convert (e.g., "my-method", "my method", "my_method")
 * @returns The camelCase version (e.g., "myMethod")
 *
 * @example
 * toMethodName("my-method") // "myMethod"
 * toMethodName("my method") // "myMethod"
 * toMethodName("my_method") // "myMethod"
 * toMethodName("MyMethod") // "mymethod"
 */
export function toMethodName<const T extends string>(input: T) {
  return input
    .split(SEPARATOR_PATTERN) // Split on hyphens, spaces, or underscores
    .filter(Boolean) // Remove empty strings from leading/trailing/consecutive separators
    .map((word, index) =>
      index === 0 ? word.toLowerCase() : word.charAt(0).toUpperCase() + word.slice(1).toLowerCase(),
    )
    .join("") as ToMethodName<typeof input>;
}
